<script setup>
import TableList from '../others/TableList.vue'
import { ref, computed } from 'vue'
import SlideDetailView from '../others/SlideDetailView.vue'
import AdForm from '../ads/AdForm.vue'
import ActionTrigger from '@/features/data/presentation/components/ActionTrigger.vue'
import ListFetcher from '@/features/data/presentation/components/ListFetcher.vue'
import { EVENTS } from '@/shared/constants/commons'
import Events from '@/events/events'
import Commons from '@/shared/utils/commons'

const detailData = ref(null)
const editAd = ref(false)
const saveAdTrigger = ref(null)
const restaurants = ref([])
const coupons = ref([])
const dishes = ref([])
const allAds = ref([])

const moveAd = async (ad, direction) => {
  const currentIndex = allAds.value.findIndex(a => a.id === ad.id)
  if (currentIndex === -1) return

  const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1
  if (newIndex < 0 || newIndex >= allAds.value.length) return

  const targetAd = allAds.value[newIndex]
  
  // Swap orders
  const currentOrder = ad.order || 0
  const targetOrder = targetAd.order || 0
  
  let newCurrentOrder = targetOrder
  let newTargetOrder = currentOrder
  
  if (newCurrentOrder === newTargetOrder) {
    if (direction === 'up') newCurrentOrder--
    else newCurrentOrder++
  }

  try {
    // Update current ad (envoi de l'objet complet mis à jour ou des champs requis incluant le metadata)
    await saveAdTrigger.value.trigger({
      action: 'update',
      adId: ad.id,
      ad: { ...ad, order: newCurrentOrder },
    })

    // Update target ad
    await saveAdTrigger.value.trigger({
      action: 'update',
      adId: targetAd.id,
      ad: { ...targetAd, order: newTargetOrder },
    })

    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Succès',
      body: 'Ordre mis à jour',
    })
    
    // Refresh list
    Events.dispatch('refresh-ads')
  } catch (error) {
    Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
      title: 'Erreur',
      body: "Erreur lors de la mise à jour de l'ordre",
    })
  }
}

const adsCols = computed(() => [
  {
    name: 'Ordre',
    id: 'order',
    value(el) {
      return el.order
    },
  },
  {
    name: 'Image',
    id: 'picture',
    value(el) {
      return `<img src="${el.picture || ''}" class="h-16 w-24 object-cover rounded-lg" />`
    },
  },
  {
    name: 'Audience',
    id: 'targetAudience',
    value(el) {
      const audienceMap = {
        restaurant: '<span class="text-indigo-600 font-medium">Restaurant</span>',
        client: '<span class="text-emerald-600 font-medium">Client</span>',
        delivery: '<span class="text-amber-600 font-medium">Livreur</span>',
        all: '<span class="text-gray-600 font-medium">Tous</span>',
      }
      return audienceMap[el.targetAudience] || `<span class="text-gray-500">${el.targetAudience || 'Tous'}</span>`
    },
  },
  {
    name: "Type d'action",
    id: 'actionType',
    value(el) {
      const typeMap = {
        external: '<span class="text-blue-600 font-medium">Lien externe</span>',
        internal: '<span class="text-green-600 font-medium">Page interne</span>',
        coupon: '<span class="text-purple-600 font-medium">Coupon</span>',
        dish: '<span class="text-orange-600 font-medium">Plat</span>',
        restaurant: '<span class="text-pink-600 font-medium">Restaurant</span>',
        feature: '<span class="text-cyan-600 font-medium">Fonctionnalité</span>',
      }
      return typeMap[el.actionType] || '-'
    },
  },
  {
    name: 'Détails',
    id: 'details',
    value(el) {
      if (el.actionType === 'external') {
        return el.url || '-'
      }
      if (el.actionType === 'internal') {
        const pageMap = {
          '/orders': 'Commandes',
          '/restaurants': 'Restaurants',
          '/profile': 'Profil',
          '/favorites': 'Favoris',
          '/cart': 'Panier',
        }
        return pageMap[el.internalPage] || el.internalPage || '-'
      }
      if (el.actionType === 'coupon' && el.coupon) {
        return `${el.coupon.code} - ${el.coupon.title}`
      }
      if (el.actionType === 'restaurant' && el.restaurantId) {
        if (el.restaurantName) return el.restaurantName
        const restaurant = restaurants.value.find(r => r.id === el.restaurantId)
        return restaurant ? (restaurant.storeName || restaurant.name) : el.restaurantId
      }
      if (el.actionType === 'dish') {
        if (el.dishName) return el.dishName
        const dish = dishes.value.find(d => d.id === el.dishId)
        return dish ? dish.title : el.dishId || '-'
      }
      if (el.actionType === 'feature') {
        const featureMap = {
          openCart: 'Ouvrir le panier',
          openFavorites: 'Ouvrir les favoris',
          openOrders: 'Ouvrir les commandes',
        }
        return featureMap[el.feature] || el.feature || '-'
      }
      return '-'
    },
  },
  {
    name: 'Actions',
    id: 'action',
    icon: () => '/images/icons/dots.png',
    options: el => {
      const actions = []

      actions.push({
        name: 'Modifier',
        action: 'edit',
        icon: 'pi pi-pencil',
      })

      actions.push({
        name: 'Supprimer',
        action: 'delete',
        icon: 'pi pi-trash',
      })

      return actions
    },
    onClick(action, el) {
      if (action.action === 'edit') {
        detailData.value = { ...el, mode: 'edit' }
        editAd.value = true
      } else if (action.action === 'delete') {
        confirmDeleteAd(el)
      }
    },
    value() {},
  },
])

const buttons = [
  {
    title: '&plus; Nouvelle Publicité',
    onClick() {
      detailData.value = { mode: 'create' }
      editAd.value = true
    },
  },
]

const confirmDeleteAd = async ad => {
  const res = await window.customPrompt({
    title: `Voulez-vous vraiment supprimer cette publicité ?`,
    description: 'Cette action est irréversible.',
    formFields: [],
    successText: 'Supprimer',
    cancelText: 'Annuler',
  })

  if (res.accepted) {
    try {
      await saveAdTrigger.value.trigger({
        action: 'delete',
        ad: { id: ad.id },
      })

      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        title: 'Succès',
        body: 'La publicité a été supprimée',
      })

      detailData.value = null
    } catch (error) {
      Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
        title: 'Erreur',
        body: 'Impossible de supprimer la publicité',
      })
    }
  }
}

const onAdSaved = () => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Succès',
    body: 'La publicité a été enregistrée',
  })
  editAd.value = false
  detailData.value = null
  Events.dispatch('refresh-ads')
}

const onAdError = error => {
  Events.dispatch(EVENTS.TOAST_NOTIFICATION_REQUESTED, {
    title: 'Erreur',
    body: error.result?.message || "Erreur lors de l'enregistrement",
  })
}
</script>

<template>
  <div class="p-5 h-full overflow-auto bg-gray-50">
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-gray-800 mb-2">Gestion des Publicités</h1>
      <p class="text-gray-600">Créez et gérez les publicités de l'application</p>
    </div>

    <TableList
      title="Publicités"
      dataKey="ads"
      hasSearch
      :cols="adsCols"
      :buttons="buttons"
      refreshOnEvents="refresh-ads"
      @fetched="allAds = $event.items"
    >
      <template #col-order="{ el }">
        <div class="flex items-center gap-3 group px-2 py-1 rounded hover:bg-white/50 transition-colors border border-transparent hover:border-gray-100">
          <div class="flex flex-col items-center justify-center bg-gray-100 rounded text-gray-500 font-bold min-w-8 h-8 text-sm">
            {{ el.order }}
          </div>
          <div class="flex items-center gap-1 opacity-100 lg:opacity-0 group-hover:opacity-100 transition-opacity">
            <button 
              @click.stop="moveAd(el, 'up')"
              class="p-1.5 hover:bg-primary-50 rounded-md text-gray-400 hover:text-primary transition-all active:scale-95 disabled:opacity-30"
              title="Monter"
              :disabled="allAds.indexOf(el) === 0"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 15l7-7 7 7" />
              </svg>
            </button>
            <button 
              @click.stop="moveAd(el, 'down')"
              class="p-1.5 hover:bg-primary-50 rounded-md text-gray-400 hover:text-primary transition-all active:scale-95 disabled:opacity-30"
              title="Descendre"
              :disabled="allAds.indexOf(el) === allAds.length - 1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </template>
    </TableList>

    <SlideDetailView
      :isVisible="!!detailData && editAd"
      @hide="editAd = false; detailData = null"
    >
      <AdForm
        v-if="detailData"
        :adData="detailData"
        :availableRestaurants="restaurants"
        :availableCoupons="coupons"
        :availableDishes="dishes"
        @saved="onAdSaved"
        @cancel="editAd = false; detailData = null"
      />
    </SlideDetailView>

    <div class="hidden">
       <ListFetcher
         dataKey="restaurants"
         class="hidden"
         @fetched="restaurants = $event.items"
       />
       <ListFetcher
         dataKey="coupons"
         class="hidden"
         @fetched="coupons = $event.items"
       />
       <ListFetcher
         dataKey="dishes"
         class="hidden"
         :limit="1000"
         @fetched="dishes = $event.items"
       />
     </div>

    <ActionTrigger
      dataKey="saveAd"
      ref="saveAdTrigger"
      @success="onAdSaved"
      @failed="onAdError"
    />
  </div>
</template>